/**
 * CAOS-325 — the Atlantis AI app's LANDING surface: an app home.
 *
 * Opening the AI app lands on a dashboard, not on a chat. That is what the reference does, and it is
 * the more useful thing to arrive at: what is open, what has been won, and what moved most recently.
 * The chat is one click away from the hero.
 *
 * Two rules shape everything below.
 *
 * ONE — every figure is measured over the WHOLE visible set. The reads go through `data/aggregate`
 * (CAOS-354), which counts and totals server-side under the same record access and field security a
 * list read has. Nothing here pages rows into the browser and adds them up; that would look right on
 * a workspace with six orders and be quietly wrong on the first workspace with two hundred and one.
 *
 * TWO — nothing here knows a business's vocabulary. It does not look for "Order" or for the word
 * "Accepted". It asks the workspace which of its objects keep a lifecycle — a status field whose
 * values declare an OUTCOME (CAOS-356) — and measures those. A workspace with no such object gets an
 * honest, empty dashboard rather than a grid of zeroes about objects it does not have (CAOS-437: this
 * is what makes the surface an app-agnostic overview rather than a sales dashboard wearing a generic
 * tag — a platform with no sales-shaped app installed still gets a real, honest page here).
 */
import type { OrgMetadata, ObjectMeta, FieldMeta, ValueOption } from '../metadata-source.js';
import type {
  AppHomeData,
  AppHomeKpi,
  AppHomeStage,
  AppHomeActivity,
  AppHomePending,
  AppHomePendingItem,
} from '@caos/ui';
import { fmtMoney } from '@caos/ui';
import { queryRecordsOutcome, recordDisplayName } from '../content-source.js';

/** A same-origin fetch, injectable so the tests drive this without a network. */
type FetchImpl = typeof fetch;

/** The aggregate route, same-origin, behind the host BFF proxy. */
const DATA_AGGREGATE_PATH = '/api/data/aggregate';

/** How many recent records the activity panel lists. */
const RECENT_LIMIT = 6;

/**
 * CAOS-684 — how many waiting items the lead region lists, and how many each object may contribute.
 *
 * The per-object cap exists so one very busy object cannot crowd every other one out of the list
 * before the merge even happens: the reader with two hundred open proposals should still see the one
 * order that has sat for a month.
 */
const PENDING_LIMIT = 8;
const PENDING_PER_OBJECT = 8;

/**
 * How many lifecycle-bearing objects the pending read covers. Deliberately larger than
 * {@link MAX_TILES}: that cap is the width of a tile strip, and it has no bearing on how many kinds
 * of thing a person can have left unfinished.
 *
 * It IS a limit on the claim this region makes, and worth naming plainly. An object past the cap is
 * not asked about, so "longest-untouched first" means longest among the objects consulted — a
 * workspace with more than this many lifecycle-bearing objects could hold something older in the
 * one that was not. The objects are consulted in the app author's own tab order, so what gets
 * dropped is what that author already ranked last, rather than whatever a map iterated over. The
 * cap is on what opening the app costs; changing it is a decision about that, and the suite pins
 * the number so that it has to be one.
 */
const PENDING_OBJECT_LIMIT = 12;

/**
 * The sentinel `data/query` resolves to the person reading, SERVER-SIDE (`data-routes.ts`). The
 * browser never learns or sends a principal id — it asks for "mine" and the Engine decides who that
 * is, which is the only version of this question that is not also a way to read someone else's desk.
 */
const CURRENT_USER = '$currentUser';

/** The audit column a wait is measured against. Orderable on every object (CAOS-357). */
const UPDATED_AT = 'updated_at';

/** How many headline tiles the strip shows before it stops (the reference's strip is five wide). */
const MAX_TILES = 5;

/** One bucket of a grouped measure, as the aggregate route returns it. */
interface Bucket {
  readonly key: string | null;
  readonly count: number;
  readonly value: number | null;
}

/**
 * One object in this workspace that keeps a lifecycle: a status field whose value set says what its
 * values MEAN, and (when it has one) the money field worth totalling alongside.
 */
interface Measurable {
  readonly object: ObjectMeta;
  readonly statusField: FieldMeta;
  readonly options: ReadonlyMap<string, ValueOption>;
  readonly moneyField?: FieldMeta;
  /** Where this object's records live, e.g. `/sales/orders`. Absent when no app tab shows them. */
  readonly href?: string;
}

/**
 * Which objects can be measured, and in what order.
 *
 * An object qualifies when it has a Select field whose value set declares at least one outcome. The
 * ORDER is the app author's: the tab order of the apps in the launcher. That is a real editorial
 * decision already made by whoever built the app, so the dashboard borrows it rather than inventing
 * a ranking of its own.
 *
 * ── CAOS-438: `appKey` narrows this to ONE application's own objects ─────────────────────────────
 *
 * Unscoped — the platform overview — every object in the workspace that keeps a lifecycle counts,
 * tabbed ones first. That is right for a surface whose subject is the workspace, and wrong for a
 * surface whose subject is an app: an application's landing that reports on objects the application
 * does not contain is not this app's landing, it is the platform's landing wearing this app's
 * heading. So when an app is named, the objects are the ones THAT app puts on a tab, in that app's
 * own tab order, and nothing else — an untabbed object is not appended, because an app landing's
 * claim is about the app's own composition rather than about everything installed.
 *
 * An app that names no lifecycle-bearing object gets an empty list, and the surface says so in
 * words ("nothing is waiting on you") rather than drawing a grid of zeroes. An `appKey` matching no
 * installed app gets the same empty list rather than falling back to the workspace: silently
 * widening the subject is how a landing comes to report on things it never promised to.
 *
 * The href still prefers THIS app's own tab. A record type on two apps' tabs has two addresses, and
 * the one an app's own landing should open is its own — a landing that sends a reader into a
 * different application to act on its own work has answered the wrong question.
 */
export function findMeasurables(meta: OrgMetadata, appKey?: string): Measurable[] {
  const hrefByObject = new Map<string, string>();
  const order: string[] = [];
  // The named app first, so its tabs claim each object's address before any other app's do.
  const apps = appKey
    ? [...meta.apps.filter((a) => a.key === appKey), ...meta.apps.filter((a) => a.key !== appKey)]
    : meta.apps;
  for (const app of apps) {
    const mine = !appKey || app.key === appKey;
    for (const tab of app.tabs) {
      if (!tab.object) continue;
      if (!hrefByObject.has(tab.object)) hrefByObject.set(tab.object, `/${app.key}/${tab.key}`);
      if (mine && !order.includes(tab.object)) order.push(tab.object);
    }
  }
  // Objects nothing puts on a tab still count — they are simply listed after the ones that do.
  // Scoped to an app they do NOT: see the note above on what an app landing is claiming.
  if (!appKey) for (const key of meta.objects.keys()) if (!order.includes(key)) order.push(key);

  const out: Measurable[] = [];
  for (const key of order) {
    const object = meta.objects.get(key);
    if (!object) continue;
    let found: { field: FieldMeta; options: ReadonlyMap<string, ValueOption> } | undefined;
    for (const field of object.fields.values()) {
      if (field.storageType !== 'Select' || !field.valueSet) continue;
      const options = meta.valueSets.get(field.valueSet);
      if (!options) continue;
      if (![...options.values()].some((o) => o.outcome)) continue;
      found = { field, options };
      break;
    }
    if (!found) continue;
    // The money field is the object's first currency-formatted field — what "worth" means for it.
    const moneyField = [...object.fields.values()].find((f) => f.format === 'currency');
    const href = hrefByObject.get(key);
    out.push({
      object,
      statusField: found.field,
      options: found.options,
      ...(moneyField ? { moneyField } : {}),
      ...(href ? { href } : {}),
    });
  }
  return out;
}

/** Ask the platform for one grouped measure. Fail-soft: an unreadable object contributes nothing. */
async function groupedMeasure(
  objectKey: string,
  groupBy: string,
  moneyField: string | undefined,
  fetchImpl: FetchImpl,
): Promise<Bucket[] | null> {
  const fn = moneyField ? 'sum' : 'count';
  const field = moneyField ? `&field=${encodeURIComponent(moneyField)}` : '';
  try {
    const res = await fetchImpl(
      `${DATA_AGGREGATE_PATH}?object=${encodeURIComponent(objectKey)}&fn=${fn}${field}&groupBy=${encodeURIComponent(groupBy)}`,
      { method: 'GET', headers: { accept: 'application/json' }, credentials: 'same-origin', cache: 'no-store' },
    );
    const env = (await res.json()) as { ok?: boolean; data?: { buckets?: Bucket[] } };
    if (env?.ok === true && Array.isArray(env.data?.buckets)) return env.data!.buckets!;
    return null;
  } catch {
    return null;
  }
}

/** Total up the buckets whose status carries a given outcome. */
function totalWhere(
  buckets: readonly Bucket[],
  options: ReadonlyMap<string, ValueOption>,
  outcome: 'open' | 'won' | 'lost',
): { count: number; value: number } {
  let count = 0;
  let value = 0;
  for (const b of buckets) {
    if (b.key === null) continue; // a record with no status is not open, won, or lost — it is unsaid
    if (options.get(b.key)?.outcome !== outcome) continue;
    count += b.count;
    value += b.value ?? 0;
  }
  return { count, value };
}

/** Order the buckets the way the value set is authored, so a pipeline reads Draft → … → Accepted. */
function orderedStages(
  buckets: readonly Bucket[],
  options: ReadonlyMap<string, ValueOption>,
): AppHomeStage[] {
  const authored = [...options.keys()];
  const rank = (key: string | null): number => {
    const i = key === null ? -1 : authored.indexOf(key);
    return i < 0 ? authored.length : i; // unknown / unsaid statuses sort last
  };
  return [...buckets]
    .sort((a, b) => rank(a.key) - rank(b.key))
    .map((b) => ({
      status: b.key ?? '',
      label: (b.key === null ? undefined : options.get(b.key)?.label) ?? b.key ?? 'No status',
      count: b.count,
      value: b.value ?? 0,
    }));
}

/** The headline tile for one measurable object: how much of it is still open. */
function openTile(m: Measurable, buckets: readonly Bucket[]): AppHomeKpi {
  const open = totalWhere(buckets, m.options, 'open');
  return {
    label: `Open ${m.object.pluralLabel}`,
    value: String(open.count),
    sub: m.moneyField ? `${fmtMoney(open.value)} open` : 'still open',
    ...(m.href ? { href: m.href } : {}),
  };
}

/** The win-rate tile, from the buckets of whichever object leads the strip. */
function winRateTile(m: Measurable, buckets: readonly Bucket[]): AppHomeKpi {
  const won = totalWhere(buckets, m.options, 'won');
  const lost = totalWhere(buckets, m.options, 'lost');
  const decided = won.count + lost.count;
  return {
    label: 'Win Rate',
    // Nothing decided yet is not a zero percent win rate — it is no answer, and it says so.
    value: decided === 0 ? '—' : `${Math.round((won.count / decided) * 100)}%`,
    sub: `${won.count} won · ${lost.count} lost`,
  };
}

/**
 * The newest records of the leading object, with their real status and its authored tint. "Newest"
 * is genuinely newest — ordered by when each record was made (CAOS-357), not by some business date
 * standing in for recency.
 */
async function recentRows(m: Measurable, fetchImpl: FetchImpl): Promise<AppHomeActivity[]> {
  const outcome = await queryRecordsOutcome(m.object.key, fetchImpl, RECENT_LIMIT, {
    sort: { field: 'created_at', dir: 'desc' },
  });
  if (outcome.status !== 'ok') return [];
  return outcome.records.slice(0, RECENT_LIMIT).map((record) => {
    const raw = record[m.statusField.apiName];
    const status = typeof raw === 'string' ? raw : null;
    const option = status ? m.options.get(status) : undefined;
    return {
      id: String(record['id'] ?? ''),
      label: recordDisplayName(m.object, record),
      sub: m.moneyField ? formatSub(record[m.moneyField.apiName]) : null,
      status,
      statusLabel: option?.label ?? status ?? '—',
      tone: option?.intent ?? 'muted',
      href: m.href ? `${m.href}/${String(record['id'] ?? '')}` : '',
    };
  });
}

/**
 * The money line under a recent row, when the record carries one.
 *
 * A currency field arrives as an amount and its currency together — that is how the platform keeps
 * money honest — so the number has to be taken out of that pair rather than off the field directly.
 * Read naively it stringifies to nothing useful, which is what put a dash under every row.
 */
function formatSub(raw: unknown): string | null {
  if (raw == null) return null;
  const scalar =
    typeof raw === 'object' && raw !== null && 'amount' in raw
      ? (raw as { amount: unknown }).amount
      : raw;
  const n = typeof scalar === 'number' ? scalar : Number(scalar);
  return Number.isFinite(n) ? fmtMoney(n) : null;
}

/**
 * Whether this object HAS a status meaning live work — not which ones, which is the difference
 * CAOS-1601 turns on.
 *
 * This used to return the open status values, and those values were then written into the read's
 * filter one condition at a time. That made the surface hold a second copy of a classification the
 * value set already owns, and a second copy is wrong the first time somebody adds a status: the new
 * status would be live work that this region never counted, silently and in the direction of
 * claiming the reader has less to do than they have. The Engine now resolves the classification
 * itself — the filter names the outcome — so the enumeration is gone.
 *
 * What remains is a yes/no question, and it is deliberately still asked here. It does not decide
 * WHICH records match; it decides whether the question is worth putting to the server at all. That
 * distinction is load-bearing below: this surface separates "no read was worth making" from "a read
 * came back empty", so that an object with no open status cannot vote that the reader's desk is
 * clear. Answering it from the options this surface already holds for rendering costs no request.
 */
function hasOpenStatus(m: Measurable): boolean {
  return [...m.options.values()].some((o) => o.outcome === 'open');
}

/**
 * CAOS-684 — how long a record has sat, worded.
 *
 * The wording is the HOST's job for the same reason every other string on this surface is: the
 * component would need a clock and a calendar to say it, and it is not allowed either. An unparseable
 * or absent stamp says nothing rather than guessing — a row reading "Updated 56 years ago" because
 * the value was empty is worse than a row that simply does not claim.
 */
export function waitedFor(raw: unknown, now: number): string {
  if (typeof raw !== 'string' || raw === '') return '';
  const at = Date.parse(raw);
  if (!Number.isFinite(at)) return '';
  const days = Math.floor((now - at) / 86_400_000);
  // A stamp ahead of our clock is a clock disagreement, not a negative wait. Say nothing.
  if (days < 0) return '';
  if (days === 0) return 'Updated today';
  if (days === 1) return 'Updated yesterday';
  if (days < 30) return `Updated ${days} days ago`;
  const months = Math.floor(days / 30);
  return months === 1 ? 'Updated a month ago' : `Updated ${months} months ago`;
}

/** One waiting row, with the raw instant kept beside it so the merge across objects can order them. */
interface PendingRow {
  readonly item: AppHomePendingItem;
  readonly at: number;
}

/**
 * The reader's own unfinished work in ONE object: records they own whose status is still open,
 * longest-untouched first.
 *
 * Both narrowings are server-side and neither is optional. Ownership rides `data/query`'s own
 * ownership plane rather than an ordinary field condition, because that is the only form the Engine
 * resolves to the caller. The statuses ride an `any` group (CAOS-1565) because top-level conditions
 * AND together — one entry per status would ask for the records that are Draft AND Presented, which
 * is nothing, and the region would be permanently empty for the most ordinary workspace there is.
 *
 * Three outcomes, not two. `attempted: false` is an object no read was worth making for, and it is
 * kept apart from a successful read because otherwise it VOTES: one object with no open state would
 * count as a read that worked, and a workspace where every real read failed would report a clear
 * desk on the strength of the object that was never asked about.
 */
async function pendingFor(
  m: Measurable,
  fetchImpl: FetchImpl,
  now: number,
): Promise<{ attempted: boolean; ok: boolean; rows: PendingRow[] }> {
  // An object whose lifecycle has no open state cannot hold unfinished work. Not a failure, and not
  // a success either — nothing was asked.
  if (!hasOpenStatus(m)) return { attempted: false, ok: false, rows: [] };

  const outcome = await queryRecordsOutcome(m.object.key, fetchImpl, PENDING_PER_OBJECT, {
    filters: [
      { field: 'owner_id', op: 'eq', value: CURRENT_USER },
      // CAOS-1601 — the condition NAMES the class and the Engine resolves it against the value set
      // attached to the field. What stood here was one `eq` per open status inside an `any` group,
      // built from this surface's own copy of the classification; adding a status to the value set
      // left that copy behind and the region quietly stopped counting records on the new status.
      { field: m.statusField.apiName, op: 'outcome' as const, value: 'open' },
    ],
    sort: { field: UPDATED_AT, dir: 'asc' },
  });
  if (outcome.status !== 'ok') return { attempted: true, ok: false, rows: [] };

  const rows: PendingRow[] = [];
  for (const record of outcome.records.slice(0, PENDING_PER_OBJECT)) {
    const raw = record[m.statusField.apiName];
    const status = typeof raw === 'string' ? raw : null;
    const option = status ? m.options.get(status) : undefined;
    const stamp = record[UPDATED_AT];
    const at = typeof stamp === 'string' ? Date.parse(stamp) : Number.NaN;
    const id = String(record['id'] ?? '');
    rows.push({
      // A record with no readable stamp sorts LAST rather than first: `Number.NaN` would make every
      // comparison false and leave it wherever it happened to land.
      at: Number.isFinite(at) ? at : Number.POSITIVE_INFINITY,
      item: {
        id,
        label: recordDisplayName(m.object, record),
        objectLabel: m.object.label,
        statusLabel: option?.label ?? status ?? '—',
        tone: option?.intent ?? 'muted',
        waitingLabel: waitedFor(stamp, now),
        href: m.href ? `${m.href}/${id}` : '',
      },
    });
  }
  return { attempted: true, ok: true, rows };
}

/**
 * CAOS-684 — the lead region: everything of the reader's that is still open, across every object
 * that keeps a lifecycle, longest-untouched first.
 *
 * The three answers are kept apart deliberately. No measurable object at all ⇒ `no-lifecycle`: the
 * question does not arise in this workspace. Every read failed ⇒ `unreadable`, never an empty list —
 * the ownership narrowing is one filter the Engine can refuse, and if it ever does, every object
 * fails at once and "Nothing is waiting on you" would be the confident wrong answer on every screen
 * in the org. A partial failure still reports what was read, the way the tile strip already does.
 */
export async function buildPending(
  measurables: readonly Measurable[],
  fetchImpl: FetchImpl,
  now: number,
): Promise<AppHomePending> {
  if (measurables.length === 0) return { status: 'no-lifecycle' };

  const results = await Promise.all(measurables.map((m) => pendingFor(m, fetchImpl, now)));
  // Only the objects actually ASKED about get a say. An object with no open state answers nothing,
  // and counting its silence as a working read is how "every read failed" turns into "you are clear".
  const attempted = results.filter((r) => r.attempted);
  if (attempted.length > 0 && attempted.every((r) => !r.ok)) return { status: 'unreadable' };

  const rows = results
    .flatMap((r) => r.rows)
    .sort((a, b) => a.at - b.at)
    .slice(0, PENDING_LIMIT);
  return { status: 'ok', title: 'Waiting on you', items: rows.map((r) => r.item) };
}

/**
 * Compose the surface: what is waiting on the reader, then the figures behind it.
 *
 * Every measurable object contributes a tile; the FIRST one — the one the app author put first —
 * also supplies the breakdown, the win rate, and the recent activity, because a dashboard with five
 * stacked charts is a worse answer than one clear one.
 *
 * CAOS-684 — the pending read runs ALONGSIDE the dashboard reads, not after them. It is the part a
 * reader came for, and making it wait on five aggregates would put the slowest half of the surface
 * in front of the fastest. `now` is injected so the wait wording is testable against a fixed clock
 * rather than against whatever the machine running the suite happens to think the date is.
 */
export async function buildAppHomeData(
  meta: OrgMetadata,
  fetchImpl: FetchImpl = fetch,
  now: number = Date.now(),
  /**
   * CAOS-438 — the application this landing belongs to. ABSENT ⇒ the platform overview, measured
   * over every lifecycle-bearing object in the workspace, which is every caller that existed before
   * an app could declare a landing of its own. See {@link findMeasurables} for what naming one
   * changes.
   */
  appKey?: string,
): Promise<AppHomeData> {
  const everyMeasurable = findMeasurables(meta, appKey);
  const measurables = everyMeasurable.slice(0, MAX_TILES);
  // The pending list reads more objects than the strip shows tiles: a tile is a slot in a fixed-width
  // strip, and a thing you have not finished is not. The cap is on how many reads opening the app
  // costs, not on how much of the answer is worth having.
  const pendingPromise = buildPending(everyMeasurable.slice(0, PENDING_OBJECT_LIMIT), fetchImpl, now);

  if (measurables.length === 0) {
    return {
      pending: await pendingPromise,
      kpis: [],
      pipeline: null,
      recent: { title: 'Recent activity', rows: [] },
    };
  }

  const measured = await Promise.all(
    measurables.map((m) =>
      groupedMeasure(
        m.object.key,
        `${m.object.key}.${m.statusField.apiName}`,
        m.moneyField ? `${m.object.key}.${m.moneyField.apiName}` : undefined,
        fetchImpl,
      ).then((buckets) => ({ m, buckets })),
    ),
  );

  // An object whose measure could not be read is left OUT rather than shown as a zero — a tile that
  // reads "0" when the truth is "we could not tell" is the kind of quiet lie this whole surface exists
  // to avoid.
  const usable = measured.filter((x): x is { m: Measurable; buckets: Bucket[] } => x.buckets !== null);
  if (usable.length === 0) {
    // CAOS-684 — the figures being unreadable says nothing about the pending read, which asks a
    // different route a different question. It is reported either way.
    return {
      pending: await pendingPromise,
      kpis: [],
      pipeline: null,
      recent: { title: 'Recent activity', rows: [] },
    };
  }

  const lead = usable[0]!;
  const kpis: AppHomeKpi[] = [];
  kpis.push(openTile(lead.m, lead.buckets));
  kpis.push(winRateTile(lead.m, lead.buckets));
  for (const x of usable.slice(1)) kpis.push(openTile(x.m, x.buckets));

  const rows = await recentRows(lead.m, fetchImpl);
  const all = `All ${lead.m.object.pluralLabel.toLowerCase()}`;

  return {
    pending: await pendingPromise,
    kpis: kpis.slice(0, MAX_TILES),
    pipeline: {
      title: `${lead.m.object.pluralLabel} by ${lead.m.statusField.label.toLowerCase()}`,
      ...(lead.m.href ? { linkLabel: all, linkHref: lead.m.href } : {}),
      stages: orderedStages(lead.buckets, lead.m.options),
      winLoss: {
        won: totalWhere(lead.buckets, lead.m.options, 'won'),
        lost: totalWhere(lead.buckets, lead.m.options, 'lost'),
      },
    },
    recent: {
      title: 'Recent activity',
      ...(lead.m.href ? { linkLabel: all, linkHref: lead.m.href } : {}),
      rows,
    },
  };
}

/**
 * Render the surface. Like every other content surface it returns SYNCHRONOUSLY in a loading state
 * and fills in once the reads settle, so the app opens instantly instead of holding a blank frame.
 */
export function renderAppHomeSurface(input: {
  meta: OrgMetadata;
  navigate?: (href: string) => void;
  onAsk?: () => void;
  userRole?: string;
  fetchImpl?: FetchImpl;
  /** CAOS-684 — the clock the wait wording is measured against. Injected only by the tests. */
  now?: number;
  /**
   * CAOS-438 — the application whose landing this is. ABSENT ⇒ the platform overview.
   *
   * It comes from the ADDRESS this surface was reached at, never from the page that declared it.
   * That is the whole of why an `appLanding` data source carries no properties: a page that could
   * name an app could name a DIFFERENT app, and an application's landing reporting on another
   * application's work is a false statement that would look completely ordinary on screen.
   */
  appKey?: string;
}): HTMLElement {
  const el = document.createElement('caos-app-home');
  // CAOS-649 — the hero greets by role, not by name, so there is no name to hand it.
  if (input.userRole) el.setAttribute('user-role', input.userRole);
  el.addEventListener('home-navigate', (ev) => {
    const href = (ev as CustomEvent<{ href?: string }>).detail?.href;
    if (href) input.navigate?.(href);
  });
  el.addEventListener('home-ask', () => input.onAsk?.());

  const settled = buildAppHomeData(
    input.meta,
    input.fetchImpl ?? fetch,
    input.now ?? Date.now(),
    input.appKey,
  )
    .then((data) => {
      (el as HTMLElement & { data: AppHomeData | null }).data = data;
    })
    .catch(() => {
      // The component's own loading state is the wrong thing to leave up forever, so an outright
      // failure resolves to an empty dashboard that says there is nothing to show. CAOS-684 — the
      // pending region says `unreadable` here rather than "nothing is waiting on you": this branch
      // is reached BECAUSE the composition threw, so it is the one place that certainly did not
      // establish that the reader's desk is clear.
      (el as HTMLElement & { data: AppHomeData | null }).data = {
        pending: { status: 'unreadable' },
        kpis: [],
        pipeline: null,
        recent: { title: 'Recent activity', rows: [] },
      };
    });
  Object.defineProperty(el, APP_HOME_SETTLED, { value: settled, enumerable: false });
  return el;
}

/** The promise the tests await to know the figures have landed. */
export const APP_HOME_SETTLED = Symbol.for('caos.app-home.settled');

/** Await a rendered surface's data read (a no-op for a node that carries none). */
export function whenAppHomeSettled(node: Node | undefined): Promise<void> {
  const settled = node ? (node as unknown as Record<symbol, unknown>)[APP_HOME_SETTLED] : undefined;
  return settled instanceof Promise ? settled : Promise.resolve();
}
